import { useNavigate } from "react-router-dom";
import { useI18n } from "../i18n";
import { HelpCircle, Trophy, Calendar, Target, Users, Bell, Globe2, KeySquare, Smartphone, Award, ArrowLeft, Zap, UserCog, Activity } from "lucide-react";

/* Bilingual (EN/SV) content blocks. Each `section` is rendered as a card.
   Adding a new section here lights it up in both languages automatically
   as long as the key exists in `i18n/index.jsx`. */
const CONTENT = {
    en: [
        {
            icon: KeySquare, title: "Signing in",
            body: [
                "You can sign in with your email + password, or request a one-tap magic link sent to your inbox (works on the same browser the link is opened in).",
                "First-timers register at /register. Anyone with an invite code can join a pool from the Dashboard.",
            ],
        },
        {
            icon: Users, title: "Pools (leagues)",
            body: [
                "A pool is a private league. The creator is the pool admin and can set the optional cash bet (amount + currency + instructions).",
                "Every member predicts every match. Points are summed per pool — leaderboards are independent across pools.",
                "Members who have marked themselves 'paid' count toward the displayed 'Total pot' (bet × paid members).",
            ],
        },
        {
            icon: Calendar, title: "How to predict",
            body: [
                "Each match has a score-stepper (home goals / away goals). Predictions lock at the moment the match kicks off.",
                "For knockout matches you can also predict a draw — when you do, an amber dropdown appears letting you pick the team you think wins on penalties / extra time. Save is disabled until you choose.",
                "Use the Fixtures tab to flip between 'Kick-off' (flat chronological list) and 'Groups' (per-round). In Groups mode the rounds containing the next 2 matches auto-expand.",
                "Edited several predictions at once? A floating 'Save all (N)' button appears.",
                "Even a 0–0 prediction must be saved — predictions are only counted once they hit the server.",
            ],
        },
        {
            icon: Zap, title: "Auto-predict by FIFA ranking",
            body: [
                "Short on time? In Fixtures (Kick-off sort), click 'Auto-predict upcoming'. Pick a scope (Next 5 / Next 10 / All upcoming) and an overwrite policy, and the engine fills every unlocked match with a baseline result driven by the current FIFA ranking of each team.",
                "Each score is then jittered by ±35% independently per side, so running auto-predict twice gives different but rank-aware scorelines (e.g. Argentina vs Mali → 3-0, then 2-1, then 4-0). Underdogs still mostly lose — but they sometimes nick a goal.",
                "On knockout draws, the higher-ranked team is auto-picked as the penalty winner. You can always override any auto-filled prediction by hand before kick-off.",
                "Auto-predict skips knockout slots that don't yet have both teams resolved (or confidently predicted via your group picks).",
            ],
        },
        {
            icon: Trophy, title: "Delete all predictions",
            body: [
                "Made a mess and want to start over? Click 'Delete all upcoming' next to Auto-predict. A confirm modal lets you choose scope:",
                "• All upcoming — wipes both group + knockout in one go.",
                "• Knockout only — keeps your group-stage picks, clears just the knockout side.",
                "• Group stage only — keeps your knockout picks, clears just the groups.",
                "Predictions on already-started matches are never touched.",
            ],
        },
        {
            icon: Activity, title: "Live & finished match results",
            body: [
                "Once a match starts (or finishes), the match card swaps the score-stepper for two stacked readouts: a small grey 'Predicted X-Y' on top and the live or final score in bigger accent type below. A pulsing red dot marks in-play matches.",
                "Right beside the score you'll see a coloured points badge showing what your prediction earned — green for a full or near-full hit, amber/red for partial misses. The same number updates again at full-time if the score swings.",
                "Results sync from football-data.org every few minutes during the tournament; you'll get a push notification with your earned points when each match finishes (if notifications are enabled).",
            ],
        },
        {
            icon: Trophy, title: "Scoring",
            body: [
                "Per match: +2 points for the correct home-team goals, +2 for the correct away-team goals, +3 for the correct outcome (home win / draw / away win). Maximum 7 points per match.",
                "Knockout matches are scored on the 90-minute regulation result (extra-time goals don't count toward the base scoreline), PLUS +2 bonus points when you also correctly pick the team that wins on penalties or extra time. Knockout max = 9 points per match.",
                "Bonus answers add up to a further 100 points across the tournament — see the next section.",
            ],
        },
        {
            icon: Target, title: "Bonus answers",
            body: [
                "Each pool gets a 'Bonus' tab with five questions: tournament top scorer (20 pts), gold/silver/bronze medal (20 each), and most-goals-scored / most-conceded team across the group stage (10 each).",
                "Gold (Final winner), Silver (Final loser) and Bronze (3rd-place winner) auto-resolve from finished knockout matches — no admin input needed.",
                "Most goals / most conceded auto-resolve from finished group-stage matches. Ties are accepted — any of the tied teams counts as a correct pick.",
                "The top-scorer name is set manually by the pool admin and matched leniently (case- and accent-insensitive, small typos forgiven).",
            ],
        },
        {
            icon: Award, title: "Knockout bracket overlay",
            body: [
                "Once you've predicted every match of a group, the bracket shows YOUR predicted team in TBD slots (italic amber name with a 💭 prefix) plus a tooltip explaining which group the team is coming from (Annex C rules).",
                "When the real teams in a knockout match exactly match your predicted teams, the card lights up green — 'you called it!'",
            ],
        },
        {
            icon: Bell, title: "Notifications & PWA",
            body: [
                "Click the bell to enable browser push notifications. You'll get a kickoff reminder for each match plus a quick result summary.",
                "On iOS Safari or Android Chrome, choose 'Add to Home Screen' — the app installs as a PWA with full offline browsing of the schedule.",
            ],
        },
        {
            icon: Smartphone, title: "Mobile & themes",
            body: [
                "All views are responsive. Switch between dark and light themes from the top-right toggle. Language switcher next to it.",
                "Hover/long-press names on the Members list to reveal each member's email.",
            ],
        },
        {
            icon: Globe2, title: "Magic link login",
            body: [
                "Lost your password? Click 'Get magic link' on the login page — we'll email you a one-tap login link that's valid for 15 minutes and can only be used once.",
            ],
        },
        {
            icon: UserCog, title: "My profile — name, password, leave & delete",
            body: [
                "Click your name in the top-right header to open 'My profile'.",
                "Identity: edit your display name freely. Changing your email requires confirming your current password (security).",
                "Change password: enter current + new (min 6 chars) + confirm. The new password takes effect immediately and active sessions on other devices stay valid until they expire.",
                "Pool memberships: leave any pool you didn't create — this removes your membership AND wipes your predictions, bonus answers and points for that pool (it can't be undone). Pool admins can't leave their own pool; delete or hand off the pool from the pool page first.",
                "Danger zone: delete account anonymises your name and email and revokes login. Your past predictions are kept so other players' leaderboards stay accurate.",
            ],
        },
    ],
    sv: [
        {
            icon: KeySquare, title: "Logga in",
            body: [
                "Logga in med e-post + lösenord, eller begär en magic link som skickas till din e-post (måste öppnas i samma webbläsare).",
                "Nya användare registrerar sig på /register. Med en inbjudningskod kan du gå med i ett gäng via Översikten.",
            ],
        },
        {
            icon: Users, title: "Gäng (ligor)",
            body: [
                "Ett gäng är en privat liga. Skaparen är gäng-admin och kan sätta en valfri kontant insats (belopp + valuta + instruktioner).",
                "Alla medlemmar tippar alla matcher. Poäng summeras per gäng — topplistor är oberoende mellan gäng.",
                "Medlemmar som markerat sig som 'betald' räknas in i 'Total pott' (insats × betalande medlemmar) som visas vid gäng-namnet.",
            ],
        },
        {
            icon: Calendar, title: "Så tippar du",
            body: [
                "Varje match har en poängväljare (hemma-mål / borta-mål). Tipset låses i samma sekund som matchen sparkar igång.",
                "I slutspelsmatcher kan du tippa oavgjort — då dyker en gul rullgardin upp där du väljer vilket lag du tror vinner på straffar/förlängning. Spara aktiveras först när du valt.",
                "Använd fliken Matcher för att växla mellan 'Avspark' (kronologisk lista) och 'Grupper' (per omgång). I gruppvy expanderas omgångarna med nästa 2 matcher automatiskt.",
                "Ändrat flera tips på en gång? En 'Spara alla (N)'-knapp dyker upp.",
                "Även 0–0 måste sparas — ett tips räknas först när det nått servern.",
            ],
        },
        {
            icon: Zap, title: "Auto-tipp via FIFA-rankning",
            body: [
                "Ont om tid? Klicka 'Auto-tipp kommande' i fliken Matcher (sortering Avspark). Välj omfång (Nästa 5 / Nästa 10 / Alla kommande) och om befintliga tips ska skrivas över — sen fyller motorn alla olåsta matcher med ett grundresultat baserat på lagens aktuella FIFA-rankning.",
                "Varje delpoäng slumpas oberoende med ±35 % — så två auto-tipp-körningar ger olika men rankningsmedvetna resultat (t.ex. Argentina–Mali → 3-0, sen 2-1, sen 4-0). Underdogs förlorar oftast — men ibland nickar de in en boll.",
                "Vid slutspelsoavgjort väljs det högre rankade laget automatiskt som straffvinnare. Du kan alltid finjustera ett auto-fyllt tips manuellt före avspark.",
                "Auto-tipp hoppar över slutspelsmatcher där båda lagen inte är spikade (eller säkert förutsagda via dina gruppvalspel).",
            ],
        },
        {
            icon: Trophy, title: "Radera tips i bulk",
            body: [
                "Vill du börja om? Klicka 'Radera alla kommande' bredvid Auto-tipp. En dialog låter dig välja omfång:",
                "• Alla kommande — torkar både gruppspel + slutspel.",
                "• Endast slutspel — sparar dina gruppspels-tips, raderar bara slutspelsvalen.",
                "• Endast gruppspel — sparar dina slutspelsvalen, raderar bara gruppspels-tipsen.",
                "Tips på matcher som redan startat rörs aldrig.",
            ],
        },
        {
            icon: Activity, title: "Live- och slutresultat",
            body: [
                "När en match startar (eller avslutas) byts poängväljaren ut mot två rader: 'Tipp X-Y' i smått ovanför och det aktuella live- eller slutresultatet i större accentfärg under. En pulserande röd prick markerar pågående matcher.",
                "Bredvid resultatet visas en färgad poängbricka med vad ditt tips gav — grön för full eller nästan full träff, gul/röd för delvis fel. Siffran uppdateras igen om resultatet svänger till slutsignal.",
                "Resultaten synkas från football-data.org var några minuter under turneringen; om push-notiser är på får du också ett meddelande med dina inkasserade poäng när varje match är klar.",
            ],
        },
        {
            icon: Trophy, title: "Poängsystem",
            body: [
                "Per match: +2 poäng för rätt antal hemmamål, +2 för rätt antal bortamål, +3 för rätt tecken (hemmavinst / oavgjort / bortavinst). Max 7 poäng per match.",
                "Slutspelsmatcher poängsätts på det ordinarie 90-minutersresultatet (mål i förlängning räknas inte mot grundresultatet), PLUS +2 bonuspoäng om du även har tippat rätt lag som vinner på straffar eller efter förlängning. Slutspelsmax = 9 poäng per match.",
                "Bonusfrågorna kan ge ytterligare upp till 100 poäng under turneringen — se nästa avsnitt.",
            ],
        },
        {
            icon: Target, title: "Bonusfrågor",
            body: [
                "Varje gäng har en Bonus-flik med fem frågor: skyttekung (20p), guld/silver/brons (20p vardera), samt mest gjorda / mest insläppta mål under gruppspelet (10p vardera).",
                "Guld (finalvinnare), silver (finalförlorare) och brons (vinnare av bronsmatchen) hämtas automatiskt från färdigspelade slutspelsmatcher — inget admin-arbete krävs.",
                "Mest gjorda / mest insläppta mål hämtas automatiskt från gruppspelets resultat. Vid delad ledning räknas alla delade lag — vilket som helst av dem är rätt.",
                "Skyttekungens namn sätts manuellt av gäng-admin och matchas tolerant (gemener/versaler, accenter och små stavfel förlåts).",
            ],
        },
        {
            icon: Award, title: "Slutspelsöversikt",
            body: [
                "När du tippat alla matcher i en grupp visas DIN tippade lag i TBD-rutor (kursiv gul text med 💭) plus en tooltip som visar varifrån laget kommer (Annex C-regler).",
                "När de verkliga lagen i en slutspelsmatch exakt matchar din prognos lyser kortet grönt — 'du sa det!'",
            ],
        },
        {
            icon: Bell, title: "Notiser & PWA",
            body: [
                "Klicka på klockan för att aktivera push-notiser. Du får en påminnelse vid avspark plus en snabb resultatsammanfattning.",
                "På iOS Safari eller Android Chrome — välj 'Lägg till på hemskärm' — så installeras appen som en PWA med fullt offline-läge för spelschemat.",
            ],
        },
        {
            icon: Smartphone, title: "Mobil & teman",
            body: [
                "Alla vyer fungerar på mobil. Växla mellan mörkt och ljust tema uppe till höger. Språkväxlaren ligger bredvid.",
                "Hovra/håll inne på ett namn i medlemslistan för att se medlemmens e-post.",
            ],
        },
        {
            icon: Globe2, title: "Magic link-inloggning",
            body: [
                "Glömt lösenordet? Klicka 'Få magic link' på inloggningssidan — vi mailar en engångslänk som gäller i 15 minuter.",
            ],
        },
        {
            icon: UserCog, title: "Min profil — namn, lösenord, lämna & radera",
            body: [
                "Klicka på ditt namn uppe till höger för att öppna 'Min profil'.",
                "Identitet: ändra ditt visningsnamn när du vill. För att byta e-post krävs ditt nuvarande lösenord (säkerhet).",
                "Byt lösenord: ange nuvarande + nytt (minst 6 tecken) + bekräftelse. Det nya lösenordet börjar gälla direkt; öppna sessioner på andra enheter förblir giltiga tills de förfaller.",
                "Tipsgäng: lämna valfritt gäng du inte själv skapat — det tar bort ditt medlemskap OCH raderar dina tips, bonussvar och poäng för det gänget (kan inte ångras). Admin kan inte lämna sitt eget gäng; radera eller överlämna gänget från gäng-sidan först.",
                "Riskzonen: radera kontot anonymiserar ditt namn och din e-post och stänger inloggningen. Dina historiska tips sparas så att övriga spelares poäng inte påverkas.",
            ],
        },
    ],
};

export default function Instructions() {
    const { lang } = useI18n();
    const nav = useNavigate();
    const sections = CONTENT[lang === "sv" ? "sv" : "en"];
    const goBack = () => {
        // Use browser history if there's a previous entry, otherwise land on the
        // dashboard for logged-in users (or the marketing landing if guest).
        if (window.history.length > 1) nav(-1);
        else nav("/");
    };
    return (
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 space-y-6" data-testid="instructions-page">
            <button
                type="button"
                onClick={goBack}
                data-testid="help-back-btn"
                className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest font-bold text-[var(--text-secondary)] hover:text-[var(--text-primary)] mb-2"
            >
                <ArrowLeft className="h-3.5 w-3.5" /> {lang === "sv" ? "Tillbaka" : "Back"}
            </button>
            <header className="mb-2">
                <p className="text-xs uppercase tracking-[0.3em] font-bold text-[var(--accent-hex)] flex items-center gap-2">
                    <HelpCircle className="h-4 w-4" /> {lang === "sv" ? "Hjälp & regler" : "Help & rules"}
                </p>
                <h1 className="font-heading text-4xl sm:text-5xl font-extrabold uppercase tracking-tight text-[var(--text-primary)] mt-1">
                    {lang === "sv" ? "Så funkar VM-tipset" : "How this works"}
                </h1>
                <p className="text-sm text-[var(--text-secondary)] mt-3">
                    {lang === "sv"
                        ? "Allt du behöver veta om hur du tippar, hur poäng räknas och vad bonusfrågorna ger."
                        : "Everything you need to know about predicting, scoring and bonus points."}
                </p>
            </header>

            {sections.map((s) => {
                const Icon = s.icon;
                return (
                    <section key={s.title} className="rounded-lg border border-[var(--border-base)] bg-[var(--bg-card)] p-5" data-testid={`help-section-${s.title.toLowerCase().replace(/\s+/g, "-")}`}>
                        <h2 className="font-heading text-lg font-extrabold uppercase tracking-tight text-[var(--text-primary)] flex items-center gap-2 mb-2">
                            <Icon className="h-5 w-5 text-[var(--accent-hex)]" />
                            {s.title}
                        </h2>
                        <ul className="space-y-2 text-sm text-[var(--text-secondary)]">
                            {s.body.map((p, i) => (
                                <li key={i} className="leading-relaxed">{p}</li>
                            ))}
                        </ul>
                    </section>
                );
            })}
        </div>
    );
}
